import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Check, Star, Zap, Crown, Rocket } from "lucide-react";

interface Plan {
  code: string;
  name: string;
  description?: string;
  monthlyPricePaise?: number;
  annualPricePaise?: number;
  annualDiscountPercent?: number;
  pricePaise?: number; // legacy support
  price?: number;
  credits?: number;
  monthlyCreditGrant?: number;
  features: string[];
  isPopular?: boolean;
  dailyGenerationLimit?: number | null;
  razorpayPlanId?: string;
}

interface PricingCardProps {
  plan: Plan;
  isCurrentPlan: boolean;
  onSelectPlan: (interval: "month" | "annual") => void;
  billingInterval?: "month" | "annual";
}

export function PricingCard({ plan, isCurrentPlan, onSelectPlan, billingInterval = "month" }: PricingCardProps) {
  const [selectedInterval, setSelectedInterval] = useState<"month" | "annual">(billingInterval);

  const getPlanIcon = (planCode: string) => {
    switch (planCode) {
      case "FREE": return <Star className="h-6 w-6" />;
      case "PRO": return <Zap className="h-6 w-6" />;
      case "ENTERPRISE": return <Rocket className="h-6 w-6" />;
      default: return <Star className="h-6 w-6" />;
    }
  };

  const getButtonText = (interval: "month" | "annual") => {
    if (isCurrentPlan) return "Current Plan";
    if (plan.code === "FREE") return "Free Plan";
    return `Choose ${interval === "annual" ? "Annual" : "Monthly"} Plan`;
  };

  const formatPrice = (paise: number) => `₹${(paise / 100).toFixed(0)}`;
  
  const getMonthlyPrice = () => {
    return plan.monthlyPricePaise || plan.pricePaise || 0;
  };
  
  const getAnnualPrice = () => {
    return plan.annualPricePaise || (getMonthlyPrice() * 12);
  };
  
  const getAnnualMonthlyPrice = () => {
    return Math.round(getAnnualPrice() / 12);
  };
  
  const getSavings = () => {
    const monthly = getMonthlyPrice() * 12;
    const annual = getAnnualPrice();
    return monthly - annual;
  };

  return (
    <Card className={`relative ${plan.isPopular ? 'border-primary' : ''}`}>
      {plan.isPopular && (
        <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2" variant="default">
          Most Popular
        </Badge>
      )}
      
      <CardHeader>
        <div className="flex items-center gap-2">
          {getPlanIcon(plan.code)}
          <CardTitle>{plan.name}</CardTitle>
        </div>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {plan.code === "FREE" ? (
          <div className="text-3xl font-bold">
            Free
          </div>
        ) : (
          <Tabs value={selectedInterval} onValueChange={(value) => setSelectedInterval(value as "month" | "annual")} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger key={`${plan.code}-tab-month`} value="month">Monthly</TabsTrigger>
              <TabsTrigger key={`${plan.code}-tab-annual`} value="annual">Annual</TabsTrigger>
            </TabsList>
            
            <TabsContent key={`${plan.code}-content-month`} value="month" className="space-y-2">
              <div className="text-3xl font-bold">
                {formatPrice(getMonthlyPrice())}
                <span className="text-lg font-normal">/month</span>
              </div>
            </TabsContent>
            
            <TabsContent key={`${plan.code}-content-annual`} value="annual" className="space-y-2">
              <div className="text-3xl font-bold">
                {formatPrice(getAnnualMonthlyPrice())}
                <span className="text-lg font-normal">/month</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Billed annually at {formatPrice(getAnnualPrice())}
              </div>
              {plan.annualDiscountPercent && plan.annualDiscountPercent > 0 && (
                <Badge variant="secondary" className="text-xs">
                  Save {plan.annualDiscountPercent}%
                </Badge>
              )}
            </TabsContent>
          </Tabs>
        )}
        
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            {(plan.credits || plan.monthlyCreditGrant || 0).toLocaleString()} monthly credits
          </div>
          {plan.features.map((feature, index) => (
            <div key={`${plan.code}-feature-${index}`} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              {feature}
            </div>
          ))}
        </div>
        
        <Button
          onClick={() => onSelectPlan(selectedInterval)}
          disabled={isCurrentPlan || plan.code === "FREE"}
          className="w-full"
          variant={plan.isPopular || plan.code === "ENTERPRISE" ? "default" : "outline"}
        >
          {getButtonText(selectedInterval)}
        </Button>
      </CardContent>
    </Card>
  );
}